import { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { AuthContext } from "../../../Context/AuthContext";
import { Authorization } from "../../../Context/Authorization";
import Preloader from "../../../shared/PreLoaders";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };
const fieldWrap = { marginBottom: 16 };
const errStyle = { fontSize: 11, color: "#E53E3E", marginTop: 4 };

const AddGO = () => {
  const initialStage = {
    documentName: "",
    documentType: "",
    documentNo: "",
    date: "",
    details: "",
    uploadDocument: null,
    circleId: "",
    divisionId: "",
    rangeId: "",
    vfcId: "",
  };

  const { userId, selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(initialStage);
  const [circles, setCircles] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [vfcs, setVfcs] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const Config = Authorization();
        const [circlesRes, divisionsRes, rangesRes] = await Promise.all([
          axios.get(`${Api}/api/circles`, Config),
          axios.get(`${Api}/api/divisions`, Config),
          axios.get(`${Api}/api/ranges`, Config),
        ]);
        setCircles(circlesRes.data || []);
        setDivisions(divisionsRes.data || []);
        setRanges(rangesRes.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Error fetching data");
      }
      setLoading(false);
    };
    fetchOptions();
  }, [Api]);

  const fetchVfc = async (jfmcTypeId) => {
    if (!jfmcTypeId) return;
    setLoading(true);
    try {
      const Config = Authorization();
      const res = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId }, Config);
      setVfcs(res.data?.vfcs || []);
    } catch (err) {
      console.error(err);
      toast.error("Error loading VFCs");
    }
    setLoading(false);
  };

  const handleSelectChange = (e) => {
    const { id, value } = e.target;
    if (value === "ALL") {
      const map = {
        circleId: circles.map((c) => c._id),
        divisionId: divisions.map((d) => d._id),
        rangeId: ranges.map((r) => r._id),
        vfcId: vfcs.map((v) => v._id),
      };
      setFormData((prev) => ({ ...prev, [id]: map[id] }));
    } else {
      setFormData((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleChange = (e) => {
    const { id, value, files } = e.target;
    setFormData((prev) => ({ ...prev, [id]: files ? files[0] : value }));
  };

  const validateForm = () => {
    const err = {};
    if (!formData.documentName) err.documentName = "Required";
    if (!formData.documentType) err.documentType = "Required";
    if (!formData.documentNo) err.documentNo = "Required";
    if (!formData.date) err.date = "Required";
    if (!formData.details) err.details = "Required";
    if (!formData.uploadDocument) err.uploadDocument = "Upload required";
    return err;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateForm();
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      toast.error("Please fill required fields");
      return;
    }
    try {
      const Config = Authorization();
      const fd = new FormData();
      Object.keys(formData).forEach((key) => {
        if (Array.isArray(formData[key])) {
          formData[key].forEach((v) => fd.append(`${key}[]`, v));
        } else {
          fd.append(key, formData[key]);
        }
      });
      const res = await axios.post(`${Api}/api/govt-order/documents`, fd, Config);
      if (res.status === 201) {
        toast.success("Document added successfully");
        setFormData(initialStage);
        setErrors({});
      }
    } catch (error) {
      console.error(error);
      toast.error("Submission failed");
    }
  };

  const focusStyle = (e) => (e.target.style.borderColor = "#4CAF50");
  const blurStyle = (e) => (e.target.style.borderColor = "#E2E8F0");

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Add Govt Document</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Document</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Add Govt Order</span>
        </div>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
            <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
              <i className="mdi mdi-file-document" />
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Govt Order Form</span>
          </div>

          <div style={{ padding: "24px 28px" }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div style={fieldWrap}>
                  <label htmlFor="circleId" style={labelStyle}>Select Circle</label>
                  <select id="circleId" value={formData.circleId} onChange={handleSelectChange}
                    style={inputStyle} onFocus={focusStyle} onBlur={blurStyle}>
                    <option value="">Select Circle</option>
                    <option value="ALL">ALL</option>
                    {circles.map((circle) => (
                      <option key={circle._id} value={circle._id}>{circle.circleName}</option>
                    ))}
                  </select>
                </div>

                <div style={fieldWrap}>
                  <label htmlFor="divisionId" style={labelStyle}>Select Division</label>
                  <select id="divisionId" value={formData.divisionId} onChange={handleSelectChange}
                    style={inputStyle} onFocus={focusStyle} onBlur={blurStyle}>
                    <option value="">Select Division</option>
                    <option value="ALL">ALL</option>
                    {divisions.map((division) => (
                      <option key={division._id} value={division._id}>{division.divisionName}</option>
                    ))}
                  </select>
                </div>

                <div style={fieldWrap}>
                  <label htmlFor="rangeId" style={labelStyle}>Select Range</label>
                  <select id="rangeId" value={formData.rangeId} onChange={handleSelectChange}
                    style={inputStyle} onFocus={focusStyle} onBlur={blurStyle}>
                    <option value="">Select Range</option>
                    <option value="ALL">ALL</option>
                    {ranges.map((range) => (
                      <option key={range._id} value={range._id}>{range.rangeName}</option>
                    ))}
                  </select>
                </div>

                <div style={fieldWrap}>
                  <label htmlFor="vfcId" style={labelStyle}>Select VFC</label>
                  <select id="vfcId" value={formData.vfcId} onChange={handleSelectChange}
                    style={inputStyle} onFocus={focusStyle} onBlur={blurStyle}>
                    <option value="">Select VFC</option>
                    <option value="ALL">ALL</option>
                    {vfcs.map((vfc) => (
                      <option key={vfc._id} value={vfc._id}>{vfc.vfcName}</option>
                    ))}
                  </select>
                </div>

                <div style={fieldWrap}>
                  <label htmlFor="documentName" style={labelStyle}>Govt Order Name</label>
                  <input id="documentName" type="text" value={formData.documentName}
                    onChange={(e) => setFormData({ ...formData, documentName: e.target.value })}
                    style={{ ...inputStyle, borderColor: errors.documentName ? "#E53E3E" : "#E2E8F0" }}
                    onFocus={focusStyle} onBlur={blurStyle} />
                  {errors.documentName && <div style={errStyle}>{errors.documentName}</div>}
                </div>

                <div style={fieldWrap}>
                  <label htmlFor="documentType" style={labelStyle}>Govt Order Type</label>
                  <select id="documentType" value={formData.documentType}
                    onChange={(e) => setFormData({ ...formData, documentType: e.target.value })}
                    style={{ ...inputStyle, borderColor: errors.documentType ? "#E53E3E" : "#E2E8F0" }}
                    onFocus={focusStyle} onBlur={blurStyle}>
                    <option value="">Select Document Type</option>
                    <option value="renewal">VFC Renewal Certificate</option>
                    <option value="bylaw">VFC Bye Law</option>
                    <option value="audit">VFC Audit Reports</option>
                    <option value="success">Success Story Reports</option>
                  </select>
                  {errors.documentType && <div style={errStyle}>{errors.documentType}</div>}
                </div>

                <div style={fieldWrap}>
                  <label htmlFor="documentNo" style={labelStyle}>Govt Order No</label>
                  <input id="documentNo" type="text" value={formData.documentNo}
                    onChange={(e) => setFormData({ ...formData, documentNo: e.target.value })}
                    style={{ ...inputStyle, borderColor: errors.documentNo ? "#E53E3E" : "#E2E8F0" }}
                    onFocus={focusStyle} onBlur={blurStyle} />
                  {errors.documentNo && <div style={errStyle}>{errors.documentNo}</div>}
                </div>

                <div style={fieldWrap}>
                  <label htmlFor="date" style={labelStyle}>Date</label>
                  <input id="date" type="date" value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    style={{ ...inputStyle, borderColor: errors.date ? "#E53E3E" : "#E2E8F0" }}
                    onFocus={focusStyle} onBlur={blurStyle} />
                  {errors.date && <div style={errStyle}>{errors.date}</div>}
                </div>

                <div style={fieldWrap}>
                  <label htmlFor="details" style={labelStyle}>Details</label>
                  <input id="details" type="text" value={formData.details}
                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                    style={{ ...inputStyle, borderColor: errors.details ? "#E53E3E" : "#E2E8F0" }}
                    onFocus={focusStyle} onBlur={blurStyle} />
                  {errors.details && <div style={errStyle}>{errors.details}</div>}
                </div>

                <div style={fieldWrap}>
                  <label htmlFor="uploadDocument" style={labelStyle}>Upload Document</label>
                  <input id="uploadDocument" type="file" onChange={handleChange}
                    style={{ ...inputStyle, borderColor: errors.uploadDocument ? "#E53E3E" : "#E2E8F0", cursor: "pointer" }}
                    onFocus={focusStyle} onBlur={blurStyle} />
                  {errors.uploadDocument && <div style={errStyle}>{errors.uploadDocument}</div>}
                </div>
              </div>

              <div style={{ marginTop: 8 }}>
                <button type="submit"
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 24px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
                  <i className="mdi mdi-check" /> Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddGO;
